import { fromManifestMap, toManifestMap } from 'client/blocks/AliceAgentAppPage/LocalizedTextInputs';
import {
    AliceAgentAppDTO,
    AliceAgentAppManifest,
    AliceExternalAppConfig,
    McpAuth,
    McpAuthType,
    McpObserveRequest,
    McpResource,
    McpTool,
} from 'client/model/aliceAgentApp';
import { UserAgreementOrDraft } from 'client/model/userAgreement';

import {
    ExternalFormState,
    resourceFromManifest,
    resourceToManifest,
    toolFromManifest,
    toolToManifest,
} from './AliceExternalAppForm.types';

const readSource = (app: AliceAgentAppDTO, isDraft: boolean) =>
    isDraft
        ? { name: app.draft.name, logo: app.draft.logo, manifest: app.draft.manifest }
        : { name: app.published?.name ?? '', logo: app.published?.logo, manifest: app.published?.manifest };

export const toFormState = (
    app: AliceAgentAppDTO,
    isDraft: boolean,
    userAgreements: UserAgreementOrDraft[],
): ExternalFormState => {
    const src = readSource(app, isDraft);
    const config = src.manifest?.config as AliceExternalAppConfig | undefined;
    return {
        name: src.name,
        logoId: src.logo?.id,
        logoUrl: src.logo?.url,
        useZora: app.useZora,
        title: fromManifestMap(src.manifest?.title),
        shortDescription: fromManifestMap(src.manifest?.shortDescription),
        prompt: fromManifestMap(config?.prompt),
        mcp: {
            endpoint: config?.mcp?.endpoint ?? '',
            serverType: config?.mcp?.serverType ?? 'streamable-http',
            authType: (config?.mcp?.auth?.type as McpAuthType | undefined) ?? 'none',
            apiKey:
                config?.mcp?.auth?.type === 'apiKey'
                    ? ((config?.mcp?.auth?.config as { key?: string } | undefined)?.key ?? '')
                    : '',
        },
        isAuthorizationRequired: src.manifest?.isAuthorizationRequired ?? false,
        tools: (config?.mcp?.tools ?? []).map(toolFromManifest),
        resources: (config?.mcp?.resources ?? []).map(resourceFromManifest),
        chatTestingEnabled: isDraft ? app.draft.chatTestingEnabled : false,
        userAgreements,
    };
};

/**
 * Mirror of the backend rule for displaying "verified" status. After a successful `observeMcp`
 * the next DTO refresh carries `manifest.config.mcp.verified=true`; the user can still mutate
 * the form before saving, so we re-check that the current form values still match the saved
 * (and verified) MCP config — any drift flips the indicator back to "not verified" without a
 * server round-trip.
 */
export const isVerifiedForCurrentForm = (app: AliceAgentAppDTO, form: ExternalFormState): boolean => {
    const config = app.draft.manifest?.config as AliceExternalAppConfig | undefined;
    const mcp = config?.mcp;
    if (!mcp?.verified) return false;
    if (mcp.endpoint !== form.mcp.endpoint) return false;
    if (mcp.serverType !== form.mcp.serverType) return false;
    if (app.useZora !== form.useZora) return false;
    const savedAuthType = (mcp.auth?.type as McpAuthType | undefined) ?? 'none';
    if (savedAuthType !== form.mcp.authType) return false;
    if (form.mcp.authType === 'apiKey') {
        const savedKey = (mcp.auth?.config as { key?: string } | undefined)?.key ?? '';
        if (savedKey !== form.mcp.apiKey) return false;
    }
    return true;
};

export const buildAuth = (form: ExternalFormState): McpAuth | null => {
    switch (form.mcp.authType) {
        case 'none':
            // Backend models McpAuth with an EXTERNAL_PROPERTY discriminator: any `type` value
            // demands a matching `config`. There is no `none` subtype, so we send null instead
            // of `{type: 'none'}` to avoid a Jackson MismatchedInputException.
            return null;
        case 'apiKey':
            return { type: 'apiKey', config: { key: form.mcp.apiKey } };
        default:
            return null;
    }
};

export const buildManifest = (form: ExternalFormState): AliceAgentAppManifest => {
    const tools: McpTool[] = form.tools.map(toolToManifest);
    const resources: McpResource[] = form.resources.map(resourceToManifest);
    const config: AliceExternalAppConfig = {
        prompt: toManifestMap(form.prompt),
        mcp: {
            endpoint: form.mcp.endpoint,
            serverType: form.mcp.serverType,
            auth: buildAuth(form),
            tools,
            resources,
        },
    };
    return {
        type: 'aliceExternalApp',
        config,
        title: toManifestMap(form.title),
        shortDescription: toManifestMap(form.shortDescription),
        isAuthorizationRequired: form.isAuthorizationRequired,
    };
};

export const buildObserveRequest = (form: ExternalFormState): McpObserveRequest => ({
    endpoint: form.mcp.endpoint,
    serverType: form.mcp.serverType,
    useZora: form.useZora,
    auth: form.mcp.authType === 'apiKey' ? { type: 'apiKey', apiKey: form.mcp.apiKey } : { type: 'none' },
});
